import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
  ICognitoStorage,
} from 'amazon-cognito-identity-js';

// SEC-22: Use sessionStorage instead of localStorage for auth tokens
// to prevent persistent XSS token theft
const tokenStorage: ICognitoStorage = {
  getItem: (key: string) => sessionStorage.getItem(key),
  setItem: (key: string, value: string) => sessionStorage.setItem(key, value),
  removeItem: (key: string) => sessionStorage.removeItem(key),
  clear: () => sessionStorage.clear(),
};

let _userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!_userPool) {
    const poolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    if (!poolId || !clientId) {
      throw new Error('Cognito is not configured (VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID missing)');
    }
    _userPool = new CognitoUserPool({
      UserPoolId: poolId,
      ClientId: clientId,
      Storage: tokenStorage,
    });
  }
  return _userPool;
}

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
const REDIRECT_URI =
  import.meta.env.VITE_COGNITO_REDIRECT_URI ||
  `${window.location.origin}/auth/callback`;

export interface AuthUser {
  email: string;
  tenantId: string;
  role: string;
  displayName: string | null;
  sub: string;
}

function parseIdToken(session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().decodePayload();
  return {
    email: payload.email as string,
    tenantId: payload['custom:tenantId'] as string,
    role: payload['custom:role'] as string,
    displayName: (payload['custom:displayName'] as string) || null,
    sub: payload.sub as string,
  };
}

export async function signIn(email: string, password: string): Promise<AuthUser> {
  const region = import.meta.env.VITE_COGNITO_USER_POOL_ID.split('_')[0];
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: clientId,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED' || data.__type?.includes('NewPasswordRequired')) {
      const cognitoUser = new CognitoUser({ Username: email, Pool: getUserPool() });
      throw { code: 'NewPasswordRequired', cognitoUser, message: 'New password required' };
    }
    throw new Error(data.message || 'Incorrect username or password.');
  }

  if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    const cognitoUser = new CognitoUser({ Username: email, Pool: getUserPool() });
    throw { code: 'NewPasswordRequired', cognitoUser, message: 'New password required' };
  }

  // Handle MFA challenge
  if (data.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
    sessionStorage.setItem('mfa_session', data.Session);
    sessionStorage.setItem('mfa_username', email);
    throw { code: 'MfaRequired', session: data.Session, username: email, message: 'MFA verification required' };
  }

  const result = data.AuthenticationResult;
  if (!result) {
    throw new Error('Authentication failed');
  }

  // Parse the ID token manually
  const idTokenPayload = JSON.parse(atob(result.IdToken.split('.')[1]));

  // Store tokens in session storage so the SDK session management still works
  const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
  const username = idTokenPayload['cognito:username'] || idTokenPayload.sub;
  sessionStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
  sessionStorage.setItem(`${keyPrefix}.${username}.idToken`, result.IdToken);
  sessionStorage.setItem(`${keyPrefix}.${username}.accessToken`, result.AccessToken);
  sessionStorage.setItem(`${keyPrefix}.${username}.refreshToken`, result.RefreshToken);

  return {
    email: idTokenPayload.email,
    tenantId: idTokenPayload['custom:tenantId'],
    role: idTokenPayload['custom:role'],
    displayName: idTokenPayload['custom:displayName'] || null,
    sub: idTokenPayload.sub,
  };
}

export function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string,
): Promise<AuthUser> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => {
        resolve(parseIdToken(session));
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

/**
 * Completes MFA challenge with TOTP code.
 */
export async function verifyMfaCode(code: string): Promise<AuthUser> {
  const region = import.meta.env.VITE_COGNITO_USER_POOL_ID.split('_')[0];
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

  const session = sessionStorage.getItem('mfa_session');
  const username = sessionStorage.getItem('mfa_username');

  if (!session || !username) {
    throw new Error('No MFA session found');
  }

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
    },
    body: JSON.stringify({
      ChallengeName: 'SOFTWARE_TOKEN_MFA',
      ClientId: clientId,
      Session: session,
      ChallengeResponses: {
        USERNAME: username,
        SOFTWARE_TOKEN_MFA_CODE: code,
      },
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Invalid MFA code');
  }

  const result = data.AuthenticationResult;
  if (!result) {
    throw new Error('MFA verification failed');
  }

  // Clear MFA session data
  sessionStorage.removeItem('mfa_session');
  sessionStorage.removeItem('mfa_username');

  // Parse the ID token manually
  const idTokenPayload = JSON.parse(atob(result.IdToken.split('.')[1]));

  // Store tokens in session storage
  const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
  const cognitoUsername = idTokenPayload['cognito:username'] || idTokenPayload.sub;
  sessionStorage.setItem(`${keyPrefix}.LastAuthUser`, cognitoUsername);
  sessionStorage.setItem(`${keyPrefix}.${cognitoUsername}.idToken`, result.IdToken);
  sessionStorage.setItem(`${keyPrefix}.${cognitoUsername}.accessToken`, result.AccessToken);
  sessionStorage.setItem(`${keyPrefix}.${cognitoUsername}.refreshToken`, result.RefreshToken);

  return {
    email: idTokenPayload.email,
    tenantId: idTokenPayload['custom:tenantId'],
    role: idTokenPayload['custom:role'],
    displayName: idTokenPayload['custom:displayName'] || null,
    sub: idTokenPayload.sub,
  };
}

export function signOut(): void {
  try {
    const currentUser = getUserPool().getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
  } catch {
    // Cognito not configured — nothing to sign out of
  }
}

export function getSession(): Promise<{ session: CognitoUserSession; user: AuthUser } | null> {
  return new Promise((resolve) => {
    let currentUser: CognitoUser | null;
    try {
      currentUser = getUserPool().getCurrentUser();
    } catch {
      resolve(null);
      return;
    }
    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve({ session, user: parseIdToken(session) });
    });
  });
}

export async function getIdToken(): Promise<string | null> {
  const result = await getSession();
  if (!result) return null;
  return result.session.getIdToken().getJwtToken();
}

/**
 * Redirects the user to the Cognito Hosted UI for Google sign-in.
 */
export function signInWithGoogle(): void {
  if (!COGNITO_DOMAIN) {
    throw new Error('VITE_COGNITO_DOMAIN is not configured');
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem('oauth_state', state);

  const url = new URL(`https://${COGNITO_DOMAIN}/oauth2/authorize`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('redirect_uri', REDIRECT_URI);
  url.searchParams.set('identity_provider', 'Google');
  url.searchParams.set('state', state);

  window.location.href = url.toString();
}

/**
 * Exchanges an authorization code from the OAuth callback for tokens.
 * Stores tokens in localStorage and returns the parsed user.
 */
export async function handleOAuthCallback(code: string): Promise<AuthUser> {
  if (!COGNITO_DOMAIN) {
    throw new Error('VITE_COGNITO_DOMAIN is not configured');
  }

  const tokenUrl = `https://${COGNITO_DOMAIN}/oauth2/token`;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    code,
  });

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token exchange failed: ${error}`);
  }

  const tokens = await response.json();
  const idTokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));

  // Store tokens in sessionStorage matching Cognito SDK format
  const keyPrefix = `CognitoIdentityServiceProvider.${CLIENT_ID}`;
  const username = idTokenPayload['cognito:username'] || idTokenPayload.sub;
  sessionStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
  sessionStorage.setItem(`${keyPrefix}.${username}.idToken`, tokens.id_token);
  sessionStorage.setItem(`${keyPrefix}.${username}.accessToken`, tokens.access_token);
  if (tokens.refresh_token) {
    sessionStorage.setItem(`${keyPrefix}.${username}.refreshToken`, tokens.refresh_token);
  }

  return {
    email: idTokenPayload.email,
    tenantId: idTokenPayload['custom:tenantId'],
    role: idTokenPayload['custom:role'],
    displayName: idTokenPayload['custom:displayName'] || null,
    sub: idTokenPayload.sub,
  };
}

/**
 * Self-service sign up — creates a Cognito user with email verification.
 */
export async function signUp(
  email: string,
  password: string,
  firmName: string,
): Promise<void> {
  const region = import.meta.env.VITE_COGNITO_USER_POOL_ID.split('_')[0];
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp',
    },
    body: JSON.stringify({
      ClientId: clientId,
      Username: email,
      Password: password,
      UserAttributes: [
        { Name: 'email', Value: email },
      ],
      ClientMetadata: {
        firmName,
      },
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Sign up failed');
  }
}

/**
 * Confirms a sign up with the verification code sent via email.
 */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  const region = import.meta.env.VITE_COGNITO_USER_POOL_ID.split('_')[0];
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmSignUp',
    },
    body: JSON.stringify({
      ClientId: clientId,
      Username: email,
      ConfirmationCode: code,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Verification failed');
  }
}

/**
 * Initiates the forgot-password flow — sends a verification code to the user's email.
 */
export async function forgotPassword(email: string): Promise<void> {
  const region = import.meta.env.VITE_COGNITO_USER_POOL_ID.split('_')[0];
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword',
    },
    body: JSON.stringify({
      ClientId: clientId,
      Username: email,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Failed to send reset code');
  }
}

/**
 * Confirms a forgot-password flow — resets the password using the verification code.
 */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const region = import.meta.env.VITE_COGNITO_USER_POOL_ID.split('_')[0];
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
    },
    body: JSON.stringify({
      ClientId: clientId,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Failed to reset password');
  }
}

/**
 * Returns true if Google SSO is configured.
 */
export function isGoogleSSOEnabled(): boolean {
  return !!COGNITO_DOMAIN;
}
